import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, PAYMENT_MODES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Upload, Pencil, Trash2, Check, X, Banknote } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import StatusPill from "@/components/StatusPill";
import HelpHint from "@/components/HelpHint";
import { useAuth } from "@/contexts/AuthContext";
import PrintPageButton from "@/components/PrintPageButton";
import PaymentReconcileSection from "@/components/PaymentReconcileSection";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Payments() {
    const { hasRole } = useAuth();
    const [searchParams] = useSearchParams();
    const [cards, setCards] = useState([]);
    const [banks, setBanks] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [monthFilter, setMonthFilter] = useState("");
    const [f, setF] = useState({
        payment_date: new Date().toISOString().slice(0, 10),
        card_id: "",
        statement_month: new Date().toISOString().slice(0, 7),
        amount_paid: "",
        payment_type: "Full Payment",
        payment_mode: "UPI",
        bank_account_id: "",
        utr: "",
        posted: false,
        posting_date: "",
        remarks: "",
        cred_txn_id: "",
        amazon_pay_txn_id: "",
        paid_from_upi_id: "",
        reward_received_amount: "",
        reward_received_notes: "",
        amazon_pay_wallet_amount: "",
        amazon_pay_cashback_used: "",
        amazon_pay_card_amount: "",
    });

    const load = useCallback(() => {
        const params = {};
        if (monthFilter) params.statement_month = monthFilter;
        api.get("/payments", { params }).then((r) => setRows(r.data));
    }, [monthFilter]);

    const togglePosted = async (p) => {
        try {
            await api.patch(`/payments/${p.id}`, { posted: !p.posted, posting_date: !p.posted ? new Date().toISOString().slice(0, 10) : null });
            toast.success(!p.posted ? "Marked reflected" : "Marked pending");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this payment? This cannot be undone.")) return;
        try {
            await api.delete(`/payments/${id}`);
            toast.success("Payment deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        api.get("/bank-accounts").then((r) => setBanks(r.data));
        const preCard = searchParams.get("card");
        if (preCard) {
            setF((s) => ({ ...s, card_id: preCard }));
            setOpen(true);
        }
    }, [searchParams]);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const numOrZero = (v) => (v === "" || v === undefined || v === null ? 0 : Number(v));
            const payload = {
                ...f,
                amount_paid: Number(f.amount_paid || 0),
                reward_received_amount: numOrZero(f.reward_received_amount),
                amazon_pay_wallet_amount: numOrZero(f.amazon_pay_wallet_amount),
                amazon_pay_cashback_used: numOrZero(f.amazon_pay_cashback_used),
                amazon_pay_card_amount: numOrZero(f.amazon_pay_card_amount),
            };
            if (!payload.bank_account_id) delete payload.bank_account_id;
            // Only send the mode-specific reference fields when they're relevant
            if (payload.payment_mode !== "CRED") delete payload.cred_txn_id;
            if (payload.payment_mode !== "Amazon Pay") {
                delete payload.amazon_pay_txn_id;
                delete payload.amazon_pay_wallet_amount;
                delete payload.amazon_pay_cashback_used;
                delete payload.amazon_pay_card_amount;
            }
            const { data } = await api.post("/payments", payload);
            toast.success("Payment recorded");
            if (data?._duplicate_warning) {
                toast.warning(data._duplicate_warning, { duration: 8000 });
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const uploadProof = async (id, file) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/payments/upload-proof/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Proof uploaded");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="payments-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Payment Tracker</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Payments made.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} payments · {fmtMoney(rows.reduce((s, r) => s + Number(r.amount_paid || 0), 0))} total</p>
                </div>
                {hasRole("admin", "finance") && (
                    <div className="flex items-center gap-2">
                        <PrintPageButton />
                        <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-payment-btn">
                                <Plus size={16} className="mr-1.5" /> Record payment
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle className="font-display text-2xl">Record a payment</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="eyebrow">Date</Label>
                                        <Input type="date" required value={f.payment_date} onChange={setInput("payment_date")} className="mt-1.5" data-testid="pf-date" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Statement Month</Label>
                                        <Input type="month" value={f.statement_month} onChange={setInput("statement_month")} className="mt-1.5" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Card</Label>
                                        <Select value={f.card_id} onValueChange={set("card_id")}>
                                            <SelectTrigger className="mt-1.5" data-testid="pf-card"><SelectValue placeholder="Select card" /></SelectTrigger>
                                            <SelectContent>
                                                {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · {c.card_name} · •••• {c.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Amount Paid</Label>
                                        <Input type="number" step="0.01" required value={f.amount_paid} onChange={setInput("amount_paid")} className="mt-1.5" data-testid="pf-amount" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Type</Label>
                                        <Select value={f.payment_type} onValueChange={set("payment_type")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Full Payment">Full Payment</SelectItem>
                                                <SelectItem value="Minimum Payment">Minimum Payment</SelectItem>
                                                <SelectItem value="Partial Payment">Partial Payment</SelectItem>
                                                <SelectItem value="Advance Payment">Advance Payment</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Mode</Label>
                                        <Select value={f.payment_mode} onValueChange={set("payment_mode")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Paid From</Label>
                                        <Select value={f.bank_account_id || "none"} onValueChange={(v) => set("bank_account_id")(v === "none" ? "" : v)}>
                                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Bank account" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— None —</SelectItem>
                                                {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · •••• {b.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">UTR / Reference</Label>
                                        <Input value={f.utr} onChange={setInput("utr")} className="mt-1.5" />
                                    </div>
                                    <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5 col-span-2">
                                        <div>
                                            <div className="text-sm inline-flex items-center">
                                                Payment reflected on card
                                                <HelpHint text="Mark Yes only after the payment appears in the card account. Until then, the item stays in 'Payment pending reflection' on Attention Today." />
                                            </div>
                                            <div className="text-[11px] text-stone-500">Set once bank confirms posting</div>
                                        </div>
                                        <Switch checked={f.posted} onCheckedChange={set("posted")} />
                                    </div>
                                </div>

                                {/* Mode-specific detail blocks */}
                                {f.payment_mode === "CRED" && (
                                    <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3 space-y-3" data-testid="pf-cred-block">
                                        <div className="text-[11px] font-semibold text-purple-900 tracking-widest uppercase">CRED payment detail</div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="eyebrow">CRED Transaction ID</Label>
                                                <Input value={f.cred_txn_id} onChange={setInput("cred_txn_id")} placeholder="CRED-XXXXXXXX" className="mt-1.5" data-testid="pf-cred-txn" />
                                            </div>
                                            <div>
                                                <Label className="eyebrow">Paid from UPI ID</Label>
                                                <Input value={f.paid_from_upi_id} onChange={setInput("paid_from_upi_id")} placeholder="you@bank" className="mt-1.5" />
                                            </div>
                                            <div>
                                                <Label className="eyebrow">Reward / cashback received</Label>
                                                <Input type="number" step="0.01" value={f.reward_received_amount} onChange={setInput("reward_received_amount")} placeholder="0" className="mt-1.5" />
                                            </div>
                                            <div>
                                                <Label className="eyebrow">Reward notes</Label>
                                                <Input value={f.reward_received_notes} onChange={setInput("reward_received_notes")} placeholder="e.g. 200 CRED coins" className="mt-1.5" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {f.payment_mode === "Amazon Pay" && (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3" data-testid="pf-amazonpay-block">
                                        <div className="text-[11px] font-semibold text-amber-900 tracking-widest uppercase">Amazon Pay detail</div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="col-span-2">
                                                <Label className="eyebrow">Amazon Pay Transaction ID</Label>
                                                <Input value={f.amazon_pay_txn_id} onChange={setInput("amazon_pay_txn_id")} placeholder="AP-XXXXXXXX" className="mt-1.5" data-testid="pf-apay-txn" />
                                            </div>
                                            <div>
                                                <Label className="eyebrow">Amazon Pay balance used</Label>
                                                <Input type="number" step="0.01" value={f.amazon_pay_wallet_amount} onChange={setInput("amazon_pay_wallet_amount")} placeholder="0" className="mt-1.5" />
                                            </div>
                                            <div>
                                                <Label className="eyebrow">Amazon Pay cashback used</Label>
                                                <Input type="number" step="0.01" value={f.amazon_pay_cashback_used} onChange={setInput("amazon_pay_cashback_used")} placeholder="0" className="mt-1.5" />
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="eyebrow inline-flex items-center">
                                                    Card amount charged
                                                    <HelpHint text="Final amount that hits the credit card outstanding — after wallet balance and cashback offsets." />
                                                </Label>
                                                <Input type="number" step="0.01" value={f.amazon_pay_card_amount} onChange={setInput("amazon_pay_card_amount")} placeholder="0" className="mt-1.5" />
                                                <div className="text-[11px] text-stone-500 mt-1">Portion actually charged to the credit card (rest was wallet / cashback).</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Label className="eyebrow">Remarks</Label>
                                    <Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" />
                                </div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="pf-submit">Record payment</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            {/* Reconciliation */}
            <PaymentReconcileSection />

            <div className="flex flex-wrap gap-3 mb-4">
                <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-[200px]" data-testid="payments-month-filter" />
                {monthFilter && <Button variant="outline" size="sm" onClick={() => setMonthFilter("")}>Clear</Button>}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Card</th>
                                <th>Month</th>
                                <th className="text-right">Amount</th>
                                <th>Type</th>
                                <th>Mode</th>
                                <th>UTR</th>
                                <th>Posted</th>
                                <th>Proof</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const c = cardMap[p.card_id];
                                return (
                                    <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                                        <td className="tabular-nums">{fmtDate(p.payment_date)}</td>
                                        <td>{c ? `${c.bank_name} · •••• ${c.last_4}` : p.last_4}</td>
                                        <td className="tabular-nums">{p.statement_month || "—"}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(p.amount_paid)}</td>
                                        <td>{p.payment_type}</td>
                                        <td>{p.payment_mode}</td>
                                        <td className="text-xs font-mono-figure">{p.utr || "—"}</td>
                                        <td>
                                            <StatusPill status={p.posted ? "Paid" : "Due Soon"} />
                                        </td>
                                        <td>
                                            {p.proof_file ? (
                                                <a href={`${BACKEND_URL}${p.proof_file}`} target="_blank" rel="noreferrer" className="text-xs underline text-stone-700">View</a>
                                            ) : (
                                                hasRole("admin", "finance") && (
                                                    <label className="text-xs underline text-stone-500 cursor-pointer">
                                                        <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadProof(p.id, e.target.files[0])} />
                                                        Upload
                                                    </label>
                                                )
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                                {hasRole("admin", "finance") && (
                                                    <>
                                                        <button
                                                            onClick={() => togglePosted(p)}
                                                            title={p.posted ? "Mark as pending" : "Mark as reflected"}
                                                            className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                            data-testid={`payment-toggle-${p.id}`}
                                                        >
                                                            {p.posted ? <X size={13} /> : <Check size={13} />}
                                                        </button>
                                                        {hasRole("admin") && (
                                                            <button onClick={() => del(p.id)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" data-testid={`payment-delete-${p.id}`}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={10} className="p-0"><EmptyState
                                    icon={Banknote}
                                    title="No payments recorded yet."
                                    hint="Record a payment once a card or bill is paid — VAULT auto-links it to the statement and updates outstanding."
                                    cta={{ to: "/payments?add=1", label: "Record a payment", testId: "empty-payments-cta" }}
                                    className="rounded-none border-0 py-12"
                                /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
